import { useState, type ReactNode } from "react";
import { NJIconButton } from "@engie-group/fluid-design-system-react";

/*
 * Mirrors production `ui/views/pages/network/general/parameters/*` (Tailwind classes noted inline):
 * group `flex flex-row` › block `w-[50%] mr-[10%]` › `mb-3` › title, subtitle, `flex items-center mt-5` › img `mr-6` + grid.
 */

const GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  alignItems: "center",
  columnGap: 8,
  minWidth: 0,
} as const;

/** `grid-cols-[auto_minmax(0,1fr)] gap-x-2`: aligns the values of consecutive `Size2MixParameterNumber` rows. */
export function Size2MixParameterGrid({ children }: { children: ReactNode }) {
  return <div style={GRID_STYLE}>{children}</div>;
}

/** Click-to-edit numeric value (production renders the value as a 64px-min button). */
export function Size2MixParameterNumber({
  label,
  value,
}: {
  label: string;
  value: number | null;
}) {
  const [saved, setSaved] = useState(value);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  return (
    <div
      style={{
        gridColumn: "span 2",
        display: "grid",
        gridTemplateColumns: "subgrid",
        alignItems: "center",
        height: 36,
        margin: "0 8px",
      }}
    >
      <div
        style={{
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={`${label} :`}
      >
        <span>{label} :</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        {editing ? (
          <form
            style={{ display: "flex", alignItems: "center" }}
            onSubmit={(event) => {
              event.preventDefault();
              setSaved(draft.trim() ? Number(draft) : null);
              setEditing(false);
            }}
          >
            <input
              aria-label={label}
              autoFocus
              type="number"
              step="any"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              style={{ width: 72, height: 32, font: "inherit" }}
            />
            <NJIconButton
              icon="check"
              label={`Enregistrer ${label}`}
              scale="xs"
              variant="tertiary"
              type="submit"
            />
            <NJIconButton
              icon="close"
              label={`Annuler ${label}`}
              scale="xs"
              variant="tertiary"
              onClick={() => {
                setDraft(saved == null ? "" : String(saved));
                setEditing(false);
              }}
            />
          </form>
        ) : (
          <div style={{ display: "flex" }}>
            <button
              type="button"
              className="size2mix-parameter-value"
              aria-label={`Modifier ${label} (${saved ?? "indéfini"})`}
              onClick={() => setEditing(true)}
            >
              <div
                style={{ display: "flex", alignItems: "center", height: 32 }}
              >
                <span>{saved ?? "-"}</span>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export interface Size2MixParameterBlockProps {
  title: string;
  subtitle?: string;
  image: string;
  /** Rows (`Size2MixParameterNumber`) shown next to the illustration. */
  children: ReactNode;
  /** Extra rows below the illustration (`mt-5`), e.g. "Température d'ambiance". */
  footer?: ReactNode;
  /** `min-h-[144px]` on the operating-characteristics blocks. */
  minHeight?: number;
}

export function Size2MixParameterBlock({
  title,
  subtitle,
  image,
  children,
  footer,
  minHeight,
}: Size2MixParameterBlockProps) {
  return (
    <div style={{ width: "50%", marginRight: "10%" }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ color: "#000", fontSize: 16, lineHeight: "20px" }}>
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              color: "var(--nj-semantic-color-text-neutral-tertiary-default)",
              fontSize: 12,
              lineHeight: "16px",
            }}
          >
            {subtitle}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: 20,
            minHeight,
          }}
        >
          <img src={image} alt="" style={{ marginRight: 24 }} />
          <Size2MixParameterGrid>{children}</Size2MixParameterGrid>
        </div>
        {footer && (
          <div style={{ marginTop: 20 }}>
            <Size2MixParameterGrid>{footer}</Size2MixParameterGrid>
          </div>
        )}
      </div>
    </div>
  );
}

/** `flex flex-row` row of parameter blocks inside an accordion section. */
export function Size2MixParameterRow({
  children,
  justifyBetween,
}: {
  children: ReactNode;
  justifyBetween?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: justifyBetween ? "space-between" : undefined,
      }}
    >
      {children}
    </div>
  );
}
